import type { Item, List, Status, Task } from "~/types/api";
import type { TabWindowState } from "../../../task.types";
import { groupItemsByStatus, applyListFilters, TASKS_PREFIX } from "../../utils/window-helpers";

type WindowDataResult<T> = {
  id: string;
  items: T[];
  originalItems: T[];
  state: TabWindowState;
};

export const buildWindowData = async <T>(
  tasks: Task[],
  pinnedState: TabWindowState,
  fetchData: (tasks: Task[]) => Promise<{ items: Item[]; statuses: Status[] }>,
  fetchLists: (workflowIds: number[]) => Promise<List[]>
): Promise<WindowDataResult<T>> => {
  if (tasks.length <= 0) throw new Error("No tasks provided");

  const { items, statuses } = await fetchData(tasks);
  const originalGroupedItems = groupItemsByStatus(items, statuses);

  let filteredItems = items;
  if (pinnedState.appliedListIds && pinnedState.appliedListIds.length > 0) {
    const workflowIds = tasks.map((t) => t.workflowId);
    const lists = await fetchLists(workflowIds);
    filteredItems = applyListFilters(items, lists, pinnedState.appliedListIds);
  }

  const groupedItems = groupItemsByStatus(filteredItems, statuses);
  const isTaskWindow = pinnedState.id.startsWith(TASKS_PREFIX);
  const title = isTaskWindow && tasks.length === 1 ? tasks[0]?.name : pinnedState.title;
  const selectedItemsData =
    pinnedState.selectedItems instanceof Set ? pinnedState.selectedItems : new Set<number>();

  return {
    id: pinnedState.id,
    items: groupedItems as T[],
    originalItems: originalGroupedItems as T[],
    state: {
      ...pinnedState,
      ...(title && { title }),
      taskIds: tasks.map((t) => t.id),
      selectedItems: selectedItemsData,
    },
  };
};
